<template>
  <div
    v-if="audioUrl"
    class="audio-player"
    :class="{ 'audio-player--compact': compact }"
    @click.stop
  >
    <div class="audio-player__container">
      <button
        class="audio-player__play-btn"
        @click="togglePlayback"
        :disabled="isLoading || hasError"
        aria-label="Play/Pause"
      >
        <Icon
          v-if="isLoading && !hideLoadingState"
          icon="mdi:loading"
          width="14"
          height="14"
          class="play-icon play-icon--spinning"
        />
        <Icon
          v-else-if="hasError"
          icon="mdi:alert-circle"
          width="14"
          height="14"
          class="play-icon"
        />
        <Icon
          v-else
          :icon="isPlaying ? 'mdi:pause' : 'mdi:play'"
          width="14"
          height="14"
          class="play-icon"
        />
      </button>
      <div class="audio-player__controls">
        <div class="audio-player__progress-wrapper">
          <div class="audio-player__progress-track">
            <div class="audio-player__progress-fill" :style="{ width: progress + '%' }"></div>
          </div>
          <input
            type="range"
            class="audio-player__progress"
            :value="progress"
            min="0"
            max="100"
            step="0.1"
            @input="seekTo"
            @click.stop
            :disabled="isLoading || hasError || duration === 0"
            aria-label="Seek"
          />
        </div>
        <div class="audio-player__time">
          <span class="time-current">{{ formattedCurrentTime }}</span>
          <span class="time-separator">/</span>
          <span class="time-total">{{ formattedAudioDuration }}</span>
        </div>
      </div>
      <div class="audio-player__volume">
        <input
          type="range"
          class="audio-player__volume-slider"
          :value="volumePercent"
          min="0"
          max="100"
          step="1"
          @input="onVolumeChange"
          @click.stop
          aria-label="Volume"
        />
        <button class="audio-player__volume-btn" @click="toggleMute" aria-label="Mute/Unmute">
          <Icon :icon="volumeIcon" width="16" height="16" class="volume-icon" />
        </button>
      </div>
      <audio
        ref="audioElement"
        :src="audioUrl"
        @loadedmetadata="onLoadedMetadata"
        @timeupdate="onTimeUpdate"
        @ended="onEnded"
        @error="onError"
        @play="isPlaying = true"
        @pause="isPlaying = false"
        @loadstart="isLoading = true"
        @canplay="isLoading = false"
        preload="metadata"
      />
      <button
        v-if="showCloseButton"
        class="audio-player__close-btn"
        @click="handleClose"
        aria-label="Close"
        type="button"
      >
        <Icon icon="mdi:close" width="14" height="14" />
      </button>
    </div>
    <div v-if="hasError" class="audio-player__error">
      <Icon icon="mdi:alert-circle" width="14" height="14" />
      <span>Unable to load audio</span>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { Icon } from "@iconify/vue";
import { computed, onUnmounted, ref, watch } from "vue";
import { useStore } from "vuex";

interface Props {
  recordingSid?: string;
  recordingUrl?: string;
  duration?: number;
  compact?: boolean;
  autoplay?: boolean;
  showCloseButton?: boolean;
  hideLoadingState?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  recordingSid: undefined,
  recordingUrl: undefined,
  duration: 0,
  compact: false,
  autoplay: false,
  showCloseButton: false,
  hideLoadingState: false,
});

const emit = defineEmits<{
  play: [];
  pause: [];
  ended: [];
  error: [error: Error];
  close: [];
}>();

const store = useStore();

const audioUrl = computed(() => {
  const token = store.getters.sessionToken;
  if (token && props.recordingSid) {
    const base = `${
      process.env.NODE_ENV === "development"
        ? process.env.VUE_APP_LOCAL_SERVER_URL
        : process.env.VUE_APP_SERVER_URL
    }/${process.env.VUE_APP_VERSION}`;
    let apiPath = `/twilio/play-recording/${props.recordingSid}`;
    if (!base.includes("/api/v1") && !base.includes("/api/")) {
      apiPath = `/api/v1${apiPath}`;
    }
    const url = `${base}${apiPath}`;
    return `${url}?token=${encodeURIComponent(token)}`;
  }
  return props.recordingUrl || null;
});

const audioElement = ref<HTMLAudioElement | null>(null);
const isPlaying = ref(false);
const currentTime = ref(0);
const duration = ref(0);
const isLoading = ref(false);
const hasError = ref(false);

const volume = ref(0.7);
const isMuted = ref(false);
const previousVolume = ref(0.7);

const progress = computed(() => {
  if (!duration.value || !isFinite(duration.value) || duration.value === 0) {
    return 0;
  }
  if (!isFinite(currentTime.value)) {
    return 0;
  }
  const calculated = (currentTime.value / duration.value) * 100;
  return Math.min(100, Math.max(0, calculated));
});

const formattedCurrentTime = computed(() => {
  const time = currentTime.value;
  const minutes = Math.floor(time / 60);
  const seconds = Math.floor(time % 60);
  return `${minutes}:${seconds.toString().padStart(2, "0")}`;
});

const formattedAudioDuration = computed(() => {
  let durationToUse = duration.value;

  if (!durationToUse || !isFinite(durationToUse) || durationToUse === 0) {
    durationToUse = props.duration || 0;
  }

  if (!durationToUse || !isFinite(durationToUse)) {
    return "0:00";
  }

  const minutes = Math.floor(durationToUse / 60);
  const seconds = Math.floor(durationToUse % 60);
  return `${minutes}:${seconds.toString().padStart(2, "0")}`;
});

const volumePercent = computed(() => {
  return Math.round(volume.value * 100);
});

const volumeIcon = computed(() => {
  if (isMuted.value || volume.value === 0) {
    return "mdi:volume-off";
  } else if (volume.value < 0.3) {
    return "mdi:volume-low";
  } else if (volume.value < 0.7) {
    return "mdi:volume-medium";
  } else {
    return "mdi:volume-high";
  }
});

const togglePlayback = async () => {
  if (!audioElement.value) return;

  try {
    if (isPlaying.value) {
      audioElement.value.pause();
      isPlaying.value = false;
      emit("pause");
    } else {
      await audioElement.value.play();
      isPlaying.value = true;
      emit("play");
    }
  } catch (error) {
    console.error("Error playing audio:", error);
    hasError.value = true;
    isPlaying.value = false;
    emit("error", error instanceof Error ? error : new Error("Unknown error"));
  }
};

const seekTo = (event: Event) => {
  if (!audioElement.value || duration.value === 0) return;
  const target = event.target as HTMLInputElement;
  const seekTime = (parseFloat(target.value) / 100) * duration.value;
  audioElement.value.currentTime = seekTime;
  currentTime.value = seekTime;
};

const onLoadedMetadata = () => {
  if (audioElement.value) {
    const audioDuration = audioElement.value.duration;
    if (isFinite(audioDuration) && audioDuration > 0) {
      duration.value = audioDuration;
    } else {
      duration.value = props.duration || 0;
    }
    isLoading.value = false;

    audioElement.value.volume = volume.value;
    audioElement.value.muted = isMuted.value;

    if (props.autoplay) {
      audioElement.value.play().catch((error) => {
        console.error("Autoplay prevented:", error);
      });
    }
  }
};

const onTimeUpdate = () => {
  if (audioElement.value) {
    currentTime.value = audioElement.value.currentTime;
  }
};

const onEnded = () => {
  isPlaying.value = false;
  currentTime.value = 0;
  if (audioElement.value) {
    audioElement.value.currentTime = 0;
  }
  emit("ended");
};

watch(audioUrl, () => {
  if (audioElement.value) {
    hasError.value = false;
    audioElement.value.load();
  }
});

let lastErroredToken: string | null = null;

const onError = (event: Event) => {
  const currentToken = store.getters.sessionToken as string | null;
  if (currentToken && currentToken !== lastErroredToken && audioElement.value) {
    lastErroredToken = currentToken;
    audioElement.value.load();
    return;
  }
  console.error("Audio error:", event);
  hasError.value = true;
  isPlaying.value = false;
  isLoading.value = false;
  if (duration.value === 0 || !isFinite(duration.value)) {
    duration.value = props.duration || 0;
  }
  emit("error", new Error("Audio playback error"));
};

const toggleMute = () => {
  if (!audioElement.value) return;

  if (isMuted.value) {
    isMuted.value = false;
    volume.value = previousVolume.value;
    audioElement.value.muted = false;
    audioElement.value.volume = volume.value;
  } else {
    previousVolume.value = volume.value;
    isMuted.value = true;
    audioElement.value.muted = true;
  }
};

const onVolumeChange = (event: Event) => {
  if (!audioElement.value) return;
  const target = event.target as HTMLInputElement;
  const newVolume = parseFloat(target.value) / 100;

  volume.value = newVolume;
  audioElement.value.volume = newVolume;

  if (newVolume > 0 && isMuted.value) {
    isMuted.value = false;
    audioElement.value.muted = false;
  }

  if (newVolume === 0) {
    isMuted.value = true;
    audioElement.value.muted = true;
  }
};

watch(
  () => [props.recordingUrl, props.recordingSid],
  () => {
    if (audioElement.value) {
      audioElement.value.pause();
      isPlaying.value = false;
      currentTime.value = 0;
      duration.value = 0;
      hasError.value = false;
      isLoading.value = true;
    }
  }
);

watch(
  () => audioElement.value?.volume,
  (newVolume) => {
    if (audioElement.value && newVolume !== undefined && !isMuted.value) {
      volume.value = newVolume;
    }
  }
);

const handleClose = () => {
  if (audioElement.value) {
    audioElement.value.pause();
  }
  emit("close");
};

onUnmounted(() => {
  if (audioElement.value) {
    audioElement.value.pause();
    audioElement.value = null;
  }
});
</script>

<style scoped>
.audio-player {
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid #f3f4f6;
}

.audio-player--compact {
  margin-top: 0;
  padding-top: 0;
  border-top: none;
}

.audio-player__container {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 12px;
  background: #ffffff;
  border-radius: 10px;
  border: 1px solid #e5e7eb;
  min-width: 280px;
  position: relative;
}

.audio-player__play-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: none;
  background: #3e3efe;
  color: #ffffff;
  cursor: pointer;
  transition: all 0.15s ease;
  flex-shrink: 0;
  padding: 0;
}

.audio-player__play-btn:hover:not(:disabled) {
  background: #2d4ed8;
}

.audio-player__play-btn:active:not(:disabled) {
  background: #1e3fb8;
  transform: scale(0.95);
}

.audio-player__play-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.audio-player__close-btn {
  position: absolute;
  top: -6px;
  right: -6px;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #f3f4f6;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100;
  transition: all 0.15s;
  padding: 0;
  flex-shrink: 0;
  color: #6b7280;
}

.audio-player__close-btn:hover {
  background: #e5e7eb;
  color: #374151;
}

.play-icon {
  color: #ffffff;
  width: 12px;
  height: 12px;
}

.play-icon--spinning {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.audio-player__controls {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.audio-player__progress-wrapper {
  position: relative;
  height: 12px;
  display: flex;
  align-items: center;
}

.audio-player__progress-track {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  left: 0;
  right: 0;
  height: 4px;
  background: #e5e7eb;
  border-radius: 2px;
  pointer-events: none;
  z-index: 1;
}

.audio-player__progress-fill {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  left: 0;
  height: 4px;
  background: #3e3efe;
  border-radius: 2px;
  pointer-events: none;
  z-index: 2;
  transition: width 0.1s linear;
}

.audio-player__progress {
  width: 100%;
  height: 12px;
  background: transparent;
  border: none !important;
  outline: none;
  -webkit-appearance: none;
  appearance: none;
  cursor: pointer;
  position: relative;
  z-index: 3;
  margin: 0;
  padding: 0;
}

.audio-player__progress:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

.audio-player__progress::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: #3e3efe;
  cursor: pointer;
  transition: transform 0.15s ease;
  border: 2px solid #ffffff;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.15);
}

.audio-player__progress::-webkit-slider-thumb:hover {
  transform: scale(1.1);
}

.audio-player__progress::-moz-range-thumb {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: #3e3efe;
  cursor: pointer;
  border: 2px solid #ffffff;
  transition: transform 0.15s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.15);
}

.audio-player__progress::-moz-range-thumb:hover {
  transform: scale(1.1);
}

.audio-player__progress::-webkit-slider-runnable-track {
  height: 12px;
  border-radius: 2px;
  background: transparent;
}

.audio-player__progress::-moz-range-track {
  height: 12px;
  border-radius: 2px;
  background: transparent;
}

.audio-player__time {
  display: flex;
  align-items: center;
  gap: 3px;
  font-size: 0.65rem;
  color: #6b7280;
  font-weight: 500;
}

.time-current {
  color: #374151;
  font-weight: 600;
}

.time-separator {
  color: #9ca3af;
}

.time-total {
  color: #9ca3af;
}

.audio-player__volume {
  display: flex;
  align-items: center;
  flex-shrink: 0;
  gap: 6px;
}

.audio-player__volume-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 4px;
  border: none;
  background: transparent;
  color: #6b7280;
  cursor: pointer;
  transition: all 0.15s ease;
  padding: 0;
}

.audio-player__volume-btn:hover {
  background: #f3f4f6;
  color: #374151;
}

.volume-icon {
  color: inherit;
}

.audio-player__volume-slider {
  width: 50px;
  height: 4px;
  background: transparent !important;
  border: none !important;
  outline: none;
  -webkit-appearance: none;
  appearance: none;
  cursor: pointer;
  margin: 0;
}

.audio-player__volume-slider::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #6b7280;
  cursor: pointer;
  transition: all 0.15s ease;
  margin-top: -3px;
  border: 2px solid #ffffff;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
}

.audio-player__volume-slider::-webkit-slider-thumb:hover {
  background: #374151;
  transform: scale(1.1);
}

.audio-player__volume-slider::-moz-range-thumb {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #6b7280;
  cursor: pointer;
  border: 2px solid #ffffff;
  transition: all 0.15s ease;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
}

.audio-player__volume-slider::-moz-range-thumb:hover {
  background: #374151;
  transform: scale(1.1);
}

.audio-player__volume-slider::-webkit-slider-runnable-track {
  height: 4px;
  border-radius: 2px;
  background: #e5e7eb;
}

.audio-player__volume-slider::-moz-range-track {
  height: 4px;
  border-radius: 2px;
  background: #e5e7eb;
}

.audio-player__error {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 8px;
  padding: 8px 12px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 6px;
  color: #dc2626;
  font-size: 0.75rem;
  font-weight: 500;
}
</style>
